import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import {SearchInput} from "./Input.tsx";

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getSortedRowModel,
} from "@tanstack/react-table";
import { UserMenu } from "./UserMenu.tsx";
import { PeriodSelector } from "./PeriodSelector.tsx";
import { DataTable } from "./DataTable.tsx";
import { HiAcademicCap } from "react-icons/hi2";
import axiosInstance from "../axiosInstance.tsx";




type User = {
  id: number;
  accounts: number;
  alive: number;
  alive30Min: number;
  invites: number;
  totalPaid: number;

};




const columnHelper = createColumnHelper<User>();

const getColumns = (isExtendedVersion) => [
  columnHelper.accessor("id", {
    header: "ID",
    cell: (info) => info.getValue() + ` (${info.row.original.username})`,
  }),
  columnHelper.accessor("accounts", {
    header: "Кол-во акк.",
    cell: (info) => info.getValue(),
  }),
  
  columnHelper.accessor("alive", {
    
    header: "Выжило, (%)",
    cell: (info) => {
      const row = info.row.original;
      
      const value = row.accounts ? ((row.alive / (row.alive + row.dead_count)) * 100) : "—";
      return Number.isNaN(value) ? "—" : value.toFixed(1);
    },
    sortingFn: (a, b, column) => {
            const rowA = a.original;
            const valueA = !isExtendedVersion ? (rowA.accounts ? ((rowA.alive / (rowA.alive + rowA.dead_count)) * 100) : 0) : rowA.alive;

              const rowB = b.original;
            const valueB = !isExtendedVersion ? (rowB.accounts ? ((rowB.alive / (rowB.alive + rowB.dead_count)) * 100) : 0) : rowB.alive;

            return valueA - valueB;
    }
  }),
  columnHelper.accessor("invites", {
    id: "invitePerAccount",
    header: "Инвайт/акк.",
    cell: (info) => {
      const row = info.row.original;
    
      return row.workingAccounts ? (row.invites / row.workingAccounts).toFixed(2) : "—";
    },
    sortingFn: (a, b, column, ) => {
            const rowA = a.original;
      const rowB = b.original;
  

      const v1 = rowA.workingAccounts ? (rowA.invites / rowA.workingAccounts).toFixed(2) : 0;
      const v2 = rowB.workingAccounts ? (rowB.invites / rowB.workingAccounts).toFixed(2) : 0;
      return v1 - v2;

    }
  }),
  columnHelper.accessor("invites", {
    id: "avgPrice",
    header: "Ср. цена, $",
    cell: (info) => {
      const row = info.row.original;
      return row.accounts ? (row.totalPaid / row.accounts).toFixed(2) : "—";
    },
    sortingFn: (a, b) => {
      const rowA = a.original;
      const rowB = b.original;
      const avgA = rowA.accounts ? rowA.totalPaid / rowA.accounts : 0;
      const avgB = rowB.accounts ? rowB.totalPaid / rowB.accounts : 0;

          const xn = Number.isNaN(avgA);
    const yn = Number.isNaN(avgB);


    if (xn && !yn) return -1;
    if (!xn && yn) return 1;

      return avgA - avgB;
    }
  }),
  
  columnHelper.accessor("invites",{
  id: "pricePerInvite",
  header: "Цена/инвайт, $",
  cell: (info) => {
    const row = info.row.original;
    return row.invites ? (row.paidInvites / row.invites).toFixed(4) : "—";
  },
  sortingFn: (a, b) => {
    const rowA = a.original;
    const rowB = b.original;

    const priceA = rowA.invites ? rowA.paidInvites / rowA.invites : 0;
    const priceB = rowB.invites ? rowB.paidInvites / rowB.invites : 0;

    

    return priceA - priceB; // всегда число
  },
  enableSorting: true, // иногда нужно явно включить сортировку
}),

  columnHelper.accessor("totalPaid", {
    header: "Выплачено, $",
    cell: (info) => `${info.getValue().toFixed(2)}`,
  }),
];









const TableContainer = styled.div`
  display: flex;
  background-color: none;
  flex-direction: column;
  gap: 12px; // расстояние между инпутом и таблицей
`;

interface ArrowIconProps {
  active?: boolean;
  width?: number;
  height?: number;
}



export const ArrowIcon: React.FC<ArrowIconProps> = ({
  width = 52,
  height = 52,
  active = false,
  onClick = () => {},
}) => {
  const [hover, setHover] = useState(false);

  const strokeColor = hover
    ? "var(--text-active-color)" // цвет при наведении
    : active
    ? "var(--text-active-color)" // активный
    : "white"; // дефолтный

  return (
    <svg
      onClick={onClick}
      width={width}
      height={height}
      viewBox="0 0 52 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", margin: 0, padding: 0, cursor: "pointer" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <path
        d="M18.0613 31.1984C18.0993 30.8561 18.1183 30.685 18.1701 30.525C18.2161 30.3831 18.281 30.2481 18.3631 30.1235C18.4557 29.9832 18.5774 29.8614 18.8209 29.6179L29.7531 18.6858C30.6674 17.7714 32.1499 17.7714 33.0642 18.6858C33.9786 19.6001 33.9786 21.0826 33.0642 21.997L22.1321 32.9291C21.8886 33.1726 21.7668 33.2943 21.6265 33.3869C21.5019 33.469 21.3669 33.5339 21.225 33.5799C21.065 33.6317 20.8938 33.6507 20.5516 33.6887L17.75 34L18.0613 31.1984Z"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};


const UsersTable: React.FC = ({data, setData, period, setPeriod, loading}) => {
  const [sorting, _setSorting] = useState<any>([]);
  const [expanded, setExpanded] = useState(false);
  const [searchId, setSearchId] = useState(''); // состояние для поиска

  const setSorting = (value) => {
    setCustomSort(null);
    _setSorting(value);
  }

  // если expanded → убираем "Выжило, %" и добавляем два extra столбца
  const columns = getColumns(expanded);

  const [customSort, setCustomSort] = useState(null);
const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const toggleRow = (id: number) => {
    setExpandedRows(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
        newSet.delete(id);
        } else {
          newSet.clear();
        newSet.add(id);
        }
        return newSet;
    });
    };

  // фильтруем данные по searchId
const filteredData = React.useMemo(() => {
  let _data = searchId
    ? data.filter(user => (user.id.toString() + ` ${user.username}`).includes(searchId))
    : data;

  // кастомная сортировка
  if (customSort) {
    const desc = customSort == "desc"
    _data = _data.filter(a => a.alive30Min != 0).sort((a, b) => {
      const diff = a.alive30Min - b.alive30Min;
      console.log(diff);
      return !desc ? diff : -diff
    });
  }

  // фильтр по invites
  const isInviteSorted = sorting.some(
    s => s.id === 'pricePerInvite' || s.id === 'invitePerAccount'
  );

    const isAliveSorted = sorting.some(
    s => s.id === 'alive' 
  );

  return _data.filter(row => {
    if (isInviteSorted) {
      return row.paidInvites !== 0;
    }
    if (isAliveSorted) {
      return (row.alive + row.dead_count) !== 0
    }
    return true;
  });
}, [searchId, data, sorting, customSort]);


  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: customSort != null,
    getSortedRowModel:  getSortedRowModel() ,
  });


  const setCustomSorting = (value: object) => {
    setSorting([]);
    setCustomSort(value)
  }

  return (
     <TableContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'bottom' }}>
    <SearchInput
      value={searchId}
      onChange={setSearchId}
      placeholder="Поиск"
    />
    <PeriodSelector onChange={setPeriod} period={period} transparent={true}/>
    </div>
   <DataTable 
   customSorting={customSort}
   setCustomSorting={setCustomSorting}
   loading={loading}
   after={(row) => <ArrowIcon onClick={() => toggleRow(row.id)} active={expandedRows.has(row.id)}/>}
   table={table}
  expandedRows={expandedRows}
  toggleRow={toggleRow}
  expanded={expanded}
  onExpandedUpdate={setExpanded}
  renderExpandedRow={(user) => (
    <UserMenu
    userId={user.id}
      countries={["Россия", "Италия", "Украина", "Франция", "Испания"]}
      allGeo={user.allGeo}
      sbGeo={user.sbGeo}
      resourceAccess={user.state === "accepted"}
      sb={user.sb}
      italy={user.italyArchive}
      onResourceAccessChange={(state) => {
                    setData(prevData =>
          prevData.map(c =>
            c.id === user.id ? { ...c, ["state"]: state ? "accepted" : "blocked" } : c
          )
        );

      }}
      onFlagChange={(key, value) => {
        axiosInstance.put("/user", {id: user.id, key: key, value: value}).then((resp) => {
            setData(prevData =>
          prevData.map(c =>
            c.id === user.id ? { ...c, [key]: value } : c
          )
        );
        })
        
      }}
      multiplier={user.multiplier}
    />
  )}
/>
    </TableContainer>
  );
};

export default UsersTable;
